import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, FileText, FolderOpen, Users, BarChart3, Settings, Laptop, Calendar, GraduationCap, Award } from 'lucide-react';
import { heroSlides } from '../data/mockData';
import { useLanguage } from '../contexts/LanguageContext';

const iconMap = {
  FileText,
  FolderOpen,
  Users,
  BarChart3,
  Settings,
  Laptop,
  Calendar,
  GraduationCap,
  Award
};

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { t, services, isRTL } = useLanguage();

  const serviceSlugMap = [
    "claims-consulting",
    "medical-records-consulting",
    "insured-patients",
    "data-analysis-consulting",
    "admin-consulting",
    "technical-consulting",
    "events-management",
    "cme-and-medical-staff-training"
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  return (
    <div>
      {/* Hero Slider */}
      <div className="relative h-[600px] overflow-hidden">
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {/* Certification slide (id 4) - show image clearly without overlay */}
            {slide.id === 4 ? (
              <div 
                className="w-full h-full bg-contain bg-center bg-no-repeat bg-white"
                style={{ backgroundImage: `url(${slide.image})` }}
              >
                {/* Logo Watermark */}
                <img 
                  src="/logo.png" 
                  alt="Target Health" 
                  className="absolute bottom-8 left-8 w-48 opacity-20"
                />
              </div>
            ) : (
              <div 
                className="w-full h-full bg-cover bg-center"
                style={{ backgroundImage: `url(${slide.image})` }}
              >
                <div className="w-full h-full bg-black bg-opacity-40 flex items-center justify-center relative">
                  {/* Logo Watermark */}
                  <img 
                    src="/logo.png" 
                    alt="Target Health" 
                    className="absolute bottom-8 left-8 w-48 opacity-20"
                  />
                  
                  <div className="text-center text-white px-4 z-10">
                    <h2 className="text-5xl font-bold mb-4">{slide.title}</h2>
                    <p className="text-xl mb-8">{slide.subtitle}</p>
                    <Link 
                      to="/about"
                      className="inline-block bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 font-semibold transition-colors"
                    >
                      {t.home.knowMore}
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Slider Controls */}
        <button
          onClick={prevSlide}
          className={`absolute ${isRTL ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 transition-all`}
        >
          {isRTL ? <ChevronRight size={32} /> : <ChevronLeft size={32} />}
        </button>
        <button
          onClick={nextSlide}
          className={`absolute ${isRTL ? 'left-4' : 'right-4'} top-1/2 -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 transition-all`}
        >
          {isRTL ? <ChevronLeft size={32} /> : <ChevronRight size={32} />}
        </button>

        {/* Dots */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentSlide ? 'bg-white scale-125' : 'bg-white bg-opacity-50'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Services Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-800 mb-2">
              {isRTL ? 'خدماتنا' : 'OUR'} <span className="text-cyan-500">{isRTL ? '' : 'SERVICES'}</span>
            </h2>
            <div className="w-24 h-1 bg-orange-500 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, idx) => {
              const iconNames = ['FileText', 'FolderOpen', 'Users', 'BarChart3', 'Settings', 'Laptop', 'Calendar', 'GraduationCap', 'Award'];
              const IconComponent = iconMap[iconNames[idx]];
              return (
                <div 
                  key={service.id}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow group"
                >
                  <div className="bg-cyan-500 w-16 h-16 flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-500 transition-colors">
                    <IconComponent size={32} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 text-center mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 text-sm text-center mb-4">
                    {service.shortDescription}
                  </p>
                  <div className="text-center">
                    <Link 
                      to={`/services/${serviceSlugMap[idx]}`}
                      className="inline-block bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 text-sm font-semibold transition-colors"
                    >
                      {t.services.learnMore} ℹ
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
